<template>
  <div class="left-pipe-diameter">
    <div class="header-row">
      <div class="title-section">
        <img src="@/assets/gisWork/SVG/title-bg.svg" class="title-bg" />
        <span class="title-text">管径管材统计</span>
      </div>
      <div class="total-value">
        <span class="number">{{ totalSum }}</span>
        <span class="unit">km</span>
      </div>
    </div>

    <!-- 左右分栏内容区域 -->
    <div class="content-row">
      <div class="left-content">
        <!-- 百分比进度条列表 -->
        <div class="progress-list">
          <div class="progress-item" v-for="(item, index) in progressData" :key="index">
            <div class="progress-content" @mouseenter="showTooltip($event, item)" @mouseleave="hideTooltip">
              <span class="percentage">{{ item.percentage }}%</span>
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: (item.percentage * 2 + 10) + '%' }"></div>
              </div>
              <span class="label">{{ item.label }}</span>
            </div>
          </div>
        </div>

        <!-- 自定义悬浮提示 -->
        <div ref="tooltip" class="custom-tooltip" v-show="tooltip.visible">
          <div class="tooltip-content">
            <div class="tooltip-title">{{ tooltip.data.label }}</div>
            <div class="tooltip-item">长度(km): {{ tooltip.data.lengthNum }}</div>
            <div class="tooltip-item">占比: {{ tooltip.data.percentage }}%</div>
          </div>
        </div>
      </div>
      <div class="right-content">
        <!-- 导出按钮 -->
        <div class="export-section">
          <button class="export-btn" @click="exportChart">
            <img src="@/assets/gisWork/SVG/export.svg" alt="导出" title="导出" />
          </button>
        </div>
        <!-- ECharts 图表 -->
        <div ref="chartContainer" class="chart-container"></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'

export default {
  name: 'LeftPipeDiameter',
  data () {
    return {
      pipelineMaterialUrl: '/pipe-network/gis/pipe-networks/workbench/pipeline-material',
      progressData: [],
      chart: null,
      totalSum: '0',
      tooltip: {
        visible: false,
        data: {}
      }
    }
  },
  mounted () {
    this.fetchPipelineData()
  },
  beforeDestroy () {
    if (this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    async fetchPipelineData () {
      try {
        const response = await this.$http.get(this.pipelineMaterialUrl)
        const apiData = response.data

        // 处理 pipeDiameterSizes 数据
        this.progressData = apiData.pipeDiameterSizes.map(item => ({
          percentage: parseFloat(item.percentage.replace('%', '')),
          label: `DN${item.pipeDiameter}`,
          lengthNum: item.lengthNum
        })).filter(item => item.percentage > 0) // 过滤掉百分比为0的数据
          .sort((a, b) => b.percentage - a.percentage) // 按百分比降序排列

        // 更新总值（带动画效果）
        this.animateNumber(parseFloat(apiData.sum))

        // 处理 pipelineMaterialTypeVos 数据并初始化图表
        this.initChart(apiData.pipelineMaterialTypeVos)

      } catch (error) {
        console.error('获取管道数据失败:', error)
      }
    },
    animateNumber (targetValue) {
      const startValue = 0
      const duration = 2000 // 动画持续时间（毫秒）
      const startTime = Date.now()

      const animate = () => {
        const elapsed = Date.now() - startTime
        const progress = Math.min(elapsed / duration, 1)

        // 使用缓动函数
        const easeOutQuart = 1 - Math.pow(1 - progress, 4)
        const currentValue = startValue + (targetValue - startValue) * easeOutQuart

        this.totalSum = currentValue.toFixed(2)

        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          this.totalSum = targetValue.toFixed(2)
        }
      }

      animate()
    },
    initChart (materialData) {
      // 销毁已存在的图表
      if (this.chart) {
        this.chart.dispose()
      }

      // 准备图表数据并按百分比降序排序
      const chartData = materialData.map(item => ({
        name: item.pipelineMaterial,
        value: parseFloat(item.percentage.replace('%', '')),
        sum: item.sum
      })).sort((a, b) => a.value - b.value) // 按百分比升序排列，从下到上递增

      // 初始化图表
      this.$nextTick(() => {
        this.chart = echarts.init(this.$refs.chartContainer)

        const option = {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow'
            },
            formatter: function (params) {
              const dataIndex = params[0].dataIndex
              const item = chartData[dataIndex]
              return item.name + ': ' + item.sum + 'km'
            }
          },
          grid: {
            left: '5%',
            right: '18%',
            top: '3%',
            bottom: '5%',
            containLabel: true
          },
          xAxis: {
            type: 'value',
            show: false
          },
          yAxis: {
            type: 'category',
            data: chartData.map(item => item.name),
            axisTick: {
              show: false
            },
            axisLabel: {
              fontSize: 12
            }
          },
          series: [
            {
              name: '管道材质占比',
              type: 'bar',
              barWidth: '30%',
              data: chartData.map(item => item.value),
              label: {
                show: true,
                position: 'right',
                formatter: '{c}%',
                fontSize: 12,
                color: '#0CB327'
              },
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                  { offset: 0.01, color: 'rgba(30, 227, 62, 1)' },
                  { offset: 0.99, color: 'rgba(156, 255, 229, 1)' }
                ]),
                borderRadius: [4, 4, 0, 0]
              },
              emphasis: {
                itemStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                    { offset: 0.01, color: 'rgba(30, 227, 62, 0.8)' },
                    { offset: 0.99, color: 'rgba(156, 255, 229, 0.8)' }
                  ])
                }
              }
            }
          ]
        }

        this.chart.setOption(option)
      })
    },
    exportChart () {
      const params = {
        responseType: 'arraybuffer',
      }

      const head = this.$store.getters.token
      const url = `${process.env.VUE_APP_API_BASE_URL}/pipe-network/gis/pipe-networks/exportSheetGisPipeline`
      const fileName = '管材&管径.xlsx'

      const tenantId = this.$store.getters.tenantId

      // 创建一个axios实例
      const instance = axios.create({
        // 设置基本的请求配置，如基本URL、请求超时时间等
        baseURL: url,
      })
      instance.defaults.responseType = 'arraybuffer'
      instance
        .post(url, params, {
          headers: {
            responseType: 'arraybuffer',
            Authorization: head,
            'tenantId': tenantId
          },
        })
        .then((res) => {
          console.log(res)
          const blob = new Blob([res.data], {
            type: 'application/octet-stream;charset=utf-8',
          })

          const eLink = document.createElement('a')
          eLink.download = fileName
          eLink.style.display = 'none'
          eLink.href = URL.createObjectURL(blob)
          document.body.appendChild(eLink)
          eLink.click()
          URL.revokeObjectURL(eLink.href)
          document.body.removeChild(eLink)
          this.$message.success('导出完成')
        })
        .catch(() => {
          this.$message.error('导出失败，请重试')
        })
    },
    showTooltip (event, item) {
      this.tooltip.data = item
      this.tooltip.visible = true
      this.$nextTick(() => {
        this.positionTooltip(event)
      })
    },
    hideTooltip () {
      this.tooltip.visible = false
    },
    positionTooltip (event) {
      const tooltip = this.$refs.tooltip
      if (tooltip) {
        const rect = event.target.getBoundingClientRect()
        const tooltipRect = tooltip.getBoundingClientRect()

        // 计算位置，避免超出视窗
        let left = rect.left + rect.width / 2 - tooltipRect.width / 2
        let top = rect.top - tooltipRect.height - 10

        // 边界检查
        if (left < 10) left = 10
        if (left + tooltipRect.width > window.innerWidth - 10) {
          left = window.innerWidth - tooltipRect.width - 10
        }
        if (top < 10) {
          top = rect.bottom + 10
        }

        tooltip.style.left = left + 'px'
        tooltip.style.top = top + 'px'
      }
    }
  }
}
</script>

<style lang="less" scoped>
.left-pipe-diameter {
  width: 33%;
  height: 550px;
  padding: 10px;
  padding-bottom: 20px;
  border-radius: 10px;
  background-color: white;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-section {
  position: relative;
  display: inline-block;

  .title-bg {
    width: 250px;
    height: 50px;
    object-fit: contain;
  }

  .title-text {
    position: absolute;
    top: 47%;
    left: 32%;
    transform: translate(-50%, -50%);
    color: #093d97;
    font-weight: 600;
    font-size: 14px;
    white-space: nowrap;
    z-index: 2;
  }
}

.total-value {
  font-size: 24px;
  margin-right: 10px;
  font-style: italic;

  .number {
    font-weight: 700;
    color: #0a8d97;
  }

  .unit {
    font-size: 14px;
    font-weight: 600;
    color: #92cbd7;
    margin-left: 2px;
  }
}

/* 左右分栏内容区域 */
.content-row {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  height: calc(100% - 70px);
}

.left-content {
  flex: 1;
  width: 50%;
  border-radius: 8px;
  padding: 10px;
}

.right-content {
  flex: 1;
  width: 50%;
  border-radius: 8px;
  padding: 10px;
}

.export-section {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.export-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.7;
  }

  img {
    width: 22px;
    height: 22px;
  }
}

.chart-container {
  width: 100%;
  height: calc(100% - 40px);
  min-height: 400px;
}

/* 自定义悬浮提示样式 */
.custom-tooltip {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 0;
  max-width: 200px;
}

.tooltip-content {
  padding: 8px 12px;
}

.tooltip-title {
  font-weight: bold;
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
}

.tooltip-item {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  margin-bottom: 2px;
}

.tooltip-item:last-child {
  margin-bottom: 0;
}

/* 进度条列表样式 */
.progress-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 430px;
  overflow-y: auto;
  padding-right: 5px;
}

.progress-list::-webkit-scrollbar {
  display: none;
}

.progress-item {
  background-color: rgba(255, 253, 246, 1);
  border-radius: 6px;
  padding: 8px 12px;
  box-shadow: 0px 0px 5px rgba(131, 178, 178, 0.196078431372549);
}

.progress-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.percentage {
  color: #ff8c00;
  font-weight: 600;
  font-size: 14px;
  min-width: 50px;
}

.progress-bar {
  flex: 1;
  height: 10px;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  display: flex;
  justify-content: flex-end;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, rgba(255, 233, 156, 1) 1%, rgba(247, 113, 15, 1) 99%);
  border-radius: 4px;
  transition: width 0.3s ease;
  box-shadow: 0px 0px 5px rgba(131, 178, 178, 0.196078431372549), 0px 0px 2px 1px rgba(255, 255, 255, 0.996078431372549) inset;
}

.label {
  color: #333;
  font-weight: 500;
  font-size: 14px;
  min-width: 50px;
}
</style>
